/**
 * Het tabblad Teksten: de standaardteksten, een nieuwe maken (per
 * teksttype een knop), en een bestaande bewerken of verwijderen.
 *
 * Een nieuwe tekst ontstaat in twee stappen: links een eigen tekst (of een
 * die het model op internet bij elkaar zoekt en schrijft), dan omzetten
 * naar de standaardtekst op 3F, rechts. Alleen de rechterkant wordt de
 * tekst; de linkerkant wordt bewaard als `original_text`.
 *
 * Een verhaal in vijf deelteksten heeft een eigen formulier
 * (onderdelen/VerhaalFormulier.js, de knop Nieuwe fictionele tekst): met een
 * tijdvak historische fictie (stap 17), zonder een verhaal van nu (stap 17d);
 * sinds stap 20 is er geen aparte knop meer voor historische fictie. Het
 * verhaal wordt geschreven vanuit tijdvak, plaats, themawoorden, karakters,
 * vertelperspectief en een plotsjabloon, eventueel naar het voorbeeld van een
 * ander verhaal.
 * Een bestaand fictief verhaal plakken en omzetten kan nog: dat is het
 * gewone formulier, met teksttype fictioneel.
 */
import React, { useEffect, useState } from 'react';
import { Edit2, Plus, Trash2 } from 'lucide-react';
import { beheerApi as api } from '../../../api/beheer';
import { DAKPAN, DEELTEKSTGENRES, TEKSTTYPEN, isDakpan, isVerhaal } from '../../../keuzelijsten';
import { DakpanFormulier } from '../onderdelen/DakpanFormulier';
import { VerhaalFormulier } from '../onderdelen/VerhaalFormulier';
import { LEEG, MINIMUM_WOORDEN, TekstFormulier, alineasVan, telWoorden } from '../onderdelen/TekstFormulier';


export function Teksten({ actief, meldFout }) {
  const [teksten, setTeksten] = useState([]);
  const [laden, setLaden] = useState(false);

  const [nieuwOpen, setNieuwOpen] = useState(false);
  const [nieuw, setNieuw] = useState(LEEG);
  const [bewerkt, setBewerkt] = useState(null);     // de tekst die wordt bewerkt, zoals de server hem gaf
  const [bewerking, setBewerking] = useState(LEEG);

  const [omzetten, setOmzetten] = useState(false);
  const [schrijven, setSchrijven] = useState(false);
  const [verhaalOpen, setVerhaalOpen] = useState(false);   // het formulier voor een nieuw verhaal
  const [dakpanOpen, setDakpanOpen] = useState(false);     // het formulier voor een nieuwe dakpanset (stap 22)
  const verhalen = teksten.filter(isVerhaal);

  // Elke keer dat het tabblad opengaat: de lijst opnieuw.
  useEffect(() => {
    if (!actief) return;
    setLaden(true);
    api.haalTeksten().then(setTeksten).catch(err => meldFout(err.message)).finally(() => setLaden(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- alleen bij het openen van het tabblad
  }, [actief]);

  const zetOm = async (tekst, wijzig) => {
    if (telWoorden(tekst.inputText) < MINIMUM_WOORDEN) {
      meldFout('Tekst moet minimaal 800 woorden bevatten voor omzetting');
      return;
    }
    setOmzetten(true);
    meldFout('');
    try {
      const uitkomst = await api.zetOmNaar3F(tekst.inputText, tekst.text_type);
      if (uitkomst.success) wijzig(prev => ({ ...prev, standardText: uitkomst.converted_text }));
      else meldFout(uitkomst.message || 'Tekstomzetting mislukt');
    } catch (err) {
      meldFout(err.name === 'AbortError'
        ? 'Tekstomzetting duurde te lang. Probeer het opnieuw of gebruik een kortere tekst.'
        : err.message);
    } finally {
      setOmzetten(false);
    }
  };

  const schrijf = async (tekst, wijzig) => {
    if (!tekst.titel.trim()) {
      meldFout('Voer eerst een titel in');
      return;
    }
    setSchrijven(true);
    meldFout('');
    try {
      const uitkomst = await api.schrijfTekst(tekst.titel, tekst.beschrijving);
      if (uitkomst.success) wijzig(prev => ({ ...prev, inputText: uitkomst.generated_text }));
      else meldFout(uitkomst.message || 'Tekstgeneratie mislukt');
    } catch (err) {
      meldFout(err.name === 'AbortError' ? 'Tekstgeneratie duurde te lang. Probeer het opnieuw.' : err.message);
    } finally {
      setSchrijven(false);
    }
  };

  const alsVelden = (tekst) => ({
    titel: tekst.titel,
    beschrijving: tekst.beschrijving,
    text_type: tekst.text_type,
    paragraphs: alineasVan(tekst.standardText),
    original_text: tekst.inputText,
  });

  const bewaarNieuw = async () => {
    if (!nieuw.standardText) {
      meldFout('Zet eerst de tekst om naar standaardtekst (3F)');
      return;
    }
    try {
      await api.maakTekst(alsVelden(nieuw));
      setTeksten(await api.haalTeksten());
      setNieuwOpen(false);
      setNieuw(LEEG);
    } catch (err) {
      meldFout(err.message);
    }
  };

  const begin = async (id) => {
    try {
      const detail = await api.haalTekst(id);
      setBewerkt(detail);
      setVerhaalOpen(false);
      setDakpanOpen(false);
      if (isVerhaal(detail) || isDakpan(detail)) {   // eigen formulier (VerhaalFormulier, DakpanFormulier)
        setNieuwOpen(false);
        return;
      }
      // De opgeslagen tekst links (om aan te passen en opnieuw om te zetten)
      // én rechts, zodat je meteen kunt opslaan.
      const alineas = detail.paragraphs.map(p => p.inhoud).join('\n\n');
      setBewerking({
        titel: detail.titel,
        beschrijving: detail.beschrijving || '',
        text_type: detail.text_type || 'informatief',
        inputText: alineas,
        standardText: alineas,
      });
      setNieuwOpen(false);
    } catch (err) {
      meldFout(err.message);
    }
  };

  const bewaarBewerking = async () => {
    if (!bewerking.standardText) {
      meldFout('Standaardtekst (3F) is vereist');
      return;
    }
    try {
      await api.bewaarTekst(bewerkt.id, alsVelden(bewerking));
      setTeksten(await api.haalTeksten());
      stopBewerken();
    } catch (err) {
      meldFout(err.message);
    }
  };

  const stopBewerken = () => {
    setBewerkt(null);
    setBewerking(LEEG);
  };

  const verwijder = async (id) => {
    if (!window.confirm('Weet je zeker dat je deze tekst wilt verwijderen?')) return;
    try {
      await api.verwijderTekst(id);
      setTeksten(teksten.filter(t => t.id !== id));
    } catch (err) {
      meldFout(err.message);
    }
  };

  const kiesSoort = (soort) => {
    setVerhaalOpen(false);
    setDakpanOpen(false);
    if (nieuwOpen && nieuw.text_type === soort) {
      setNieuwOpen(false);
    } else {
      setNieuw({ ...LEEG, text_type: soort });
      setNieuwOpen(true);
      setBewerkt(null);
    }
  };

  const kiesVerhaal = () => {
    setNieuwOpen(false);
    setBewerkt(null);
    setDakpanOpen(false);
    setVerhaalOpen(!verhaalOpen);
  };

  const kiesDakpan = () => {
    setNieuwOpen(false);
    setBewerkt(null);
    setVerhaalOpen(false);
    setDakpanOpen(!dakpanOpen);
  };

  /** Een verhaal opslaan: nieuw, of het verhaal dat wordt bewerkt. */
  const bewaarVerhaal = async (velden) => {
    try {
      if (bewerkt) await api.bewaarTekst(bewerkt.id, velden);
      else await api.maakTekst(velden);
      setTeksten(await api.haalTeksten());
      setVerhaalOpen(false);
      setDakpanOpen(false);
      stopBewerken();
    } catch (err) {
      meldFout(err.message);
    }
  };

  if (laden) return <p>Laden...</p>;

  return (
    <div className="card">
      <div className="mb-16">
        <h2>Teksten</h2>
        <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}>
          {TEKSTTYPEN.map(soort => {
            // De fictionele tekst is een verhaal in deelteksten, van nu of met een tijdvak (plakken kan in dat formulier).
            const verhaal = soort.value === 'fictioneel';
            const aan = verhaal ? verhaalOpen || (nieuwOpen && nieuw.text_type === 'fictioneel')
              : nieuw.text_type === soort.value && nieuwOpen;
            return (
              <button
                key={soort.value}
                className={`btn ${aan ? 'btn-success' : 'btn-primary'}`}
                onClick={() => (verhaal ? kiesVerhaal() : kiesSoort(soort.value))}
                style={{ fontSize: '13px' }}
              >
                <Plus size={14} /> {soort.knop}
              </button>
            );
          })}
          <button className={`btn ${dakpanOpen ? 'btn-success' : 'btn-primary'}`} onClick={kiesDakpan} style={{ fontSize: '13px' }}>
            <Plus size={14} /> {DAKPAN.knop}
          </button>
        </div>
      </div>

      {verhaalOpen && !bewerkt && (
        <VerhaalFormulier verhalen={verhalen} onOpslaan={bewaarVerhaal}
          onAnnuleer={() => setVerhaalOpen(false)} onPlakken={() => kiesSoort('fictioneel')} meldFout={meldFout} />
      )}

      {dakpanOpen && !bewerkt && (
        <DakpanFormulier teksten={teksten} onOpslaan={bewaarVerhaal} onAnnuleer={() => setDakpanOpen(false)} meldFout={meldFout} />
      )}

      {bewerkt && isDakpan(bewerkt) && (
        <DakpanFormulier key={bewerkt.id} bestaand={bewerkt} teksten={teksten} onOpslaan={bewaarVerhaal}
          onAnnuleer={stopBewerken} meldFout={meldFout} />
      )}

      {bewerkt && isVerhaal(bewerkt) && (
        <VerhaalFormulier key={bewerkt.id} bestaand={bewerkt} verhalen={verhalen}
          onOpslaan={bewaarVerhaal} onAnnuleer={stopBewerken} meldFout={meldFout} />
      )}

      {nieuwOpen && !bewerkt && (
        <TekstFormulier
          tekst={nieuw}
          wijzig={setNieuw}
          omzetten={omzetten}
          schrijven={schrijven}
          onOmzetten={() => zetOm(nieuw, setNieuw)}
          onSchrijven={() => schrijf(nieuw, setNieuw)}
          onOpslaan={bewaarNieuw}
          onAnnuleer={() => { setNieuwOpen(false); setNieuw(LEEG); }}
        />
      )}

      {bewerkt && !isVerhaal(bewerkt) && !isDakpan(bewerkt) && (
        <TekstFormulier
          bewerken
          tekst={bewerking}
          wijzig={setBewerking}
          omzetten={omzetten}
          schrijven={schrijven}
          onOmzetten={() => zetOm(bewerking, setBewerking)}
          onSchrijven={() => schrijf(bewerking, setBewerking)}
          onOpslaan={bewaarBewerking}
          onAnnuleer={stopBewerken}
        />
      )}

      <table>
        <thead>
          <tr>
            <th>Titel</th>
            <th>Type</th>
            <th>Niveau</th>
            <th>Paragrafen</th>
            <th>Acties</th>
          </tr>
        </thead>
        <tbody>
          {teksten.map(tekst => {
            const soort = DEELTEKSTGENRES.find(g => g.value === tekst.genre) || TEKSTTYPEN.find(t => t.value === tekst.text_type);
            return (
              <tr key={tekst.id}>
                <td>{tekst.titel}</td>
                <td>
                  <span className="badge" style={{ background: soort?.kleur || 'var(--stiller)', color: 'var(--wit)', fontSize: '11px' }}>
                    {soort?.label || tekst.text_type || 'Informatief'}
                  </span>
                </td>
                <td><span className="badge badge-primary">{tekst.bron_niveau}</span></td>
                <td>{tekst.paragraph_count}</td>
                <td>
                  <div className="flex gap-8">
                    <button className="btn btn-primary" style={{ padding: '8px 12px' }} onClick={() => begin(tekst.id)}>
                      <Edit2 size={14} />
                    </button>
                    <button className="btn btn-danger" style={{ padding: '8px 12px' }} onClick={() => verwijder(tekst.id)}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
